import { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useGridScrollToBottom } from "../../../core/hooks/useGridScrollToBottom";
import { useResponsive } from "../../../core/hooks/useResponsive";

const processLogColumns = [
  { field: "scan", headerName: "Scan#", flex: 1 },
  { field: "log", headerName: "Log Message", flex: 3 },
  { field: "sold", headerName: "Sold#", flex: 1 },
];

const ProcessLogGrid = ({ processLog }) => {
  const { isCompact } = useResponsive();

  const filteredProcessLog = useMemo(() => {
    if (!processLog || processLog.length === 0) return [];
    const soldOutIndex = processLog.findIndex(
      (log) => log.log && log.log.toLowerCase().includes("all holdings soldout")
    );
    if (soldOutIndex !== -1) return processLog.slice(0, soldOutIndex + 1);
    return processLog;
  }, [processLog]);

  useGridScrollToBottom(filteredProcessLog, "process-log-grid");

  if (isCompact) {
    // Three flex columns squeeze the log message to ~200px and clip it. The message is
    // the whole point of this panel, so on narrow screens it gets the full width and
    // the scan number and sold count become compact markers around it.
    return (
      <Box id="process-log-grid" sx={{ height: "100%", overflowY: "auto", pr: 0.25 }}>
        {filteredProcessLog.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No log lines yet.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {filteredProcessLog.map((entry, i) => (
              <Stack key={entry.scan ?? i} direction="row" spacing={0.75} alignItems="flex-start">
                <Chip
                  size="small"
                  label={entry.scan}
                  sx={{ height: 18, minWidth: 26, fontSize: "0.6rem", fontWeight: 800, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: "0.72rem", flex: 1, minWidth: 0, wordBreak: "break-word", lineHeight: 1.45 }}>
                  {entry.log}
                </Typography>
                {entry.sold ? (
                  <Typography sx={{ fontSize: "0.66rem", fontWeight: 800, color: "success.main", flexShrink: 0 }}>
                    +{entry.sold}
                  </Typography>
                ) : null}
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <div id="process-log-grid" style={{ height: "100%", width: "100%" }}>
      <DataGrid
        rows={filteredProcessLog || []}
        columns={processLogColumns}
        getRowId={(row) => row.scan ?? Math.random()}
        rowHeight={35}
        hideFooter
        sx={{ height: "100%" }}
      />
    </div>
  );
};

export default ProcessLogGrid;
